import clsx from 'clsx';
import type { ToolCallState } from '@/hooks/use-streaming-v2';
import { formatNumber } from '@/lib/format';
import { PhaseShell } from './ToolResultRendererSkeleton';
import { Skeleton } from '@/components/ui/Skeleton';

interface GoalSeekData {
  found: boolean;
  inputValue?: number;
  outputValue?: number;
  iterations?: number;
  message?: string;
}

function isGoalSeekResult(data: unknown): data is GoalSeekData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'found' in data &&
    typeof (data as GoalSeekData).found === 'boolean'
  );
}

function renderComplete(result: GoalSeekData) {
  const { found, inputValue, outputValue, iterations, message } = result;
  return (
    <div className={clsx(
      'border rounded-[3px] px-3 py-2.5 space-y-2',
      found ? 'border-accent/30 bg-accent-faint' : 'border-warn/30 bg-warn-dim',
    )}>
      <div className="flex items-center gap-2">
        <div className={clsx(
          'w-2 h-2 rounded-full',
          found ? 'bg-accent shadow-[0_0_6px_var(--color-accent)]' : 'bg-warn',
        )} />
        <div className={clsx(
          'text-xs font-mono font-semibold tracking-[1.2px] uppercase',
          found ? 'text-accent' : 'text-warn',
        )}>
          {found ? 'Goal Seek: Found' : 'Goal Seek: Not Converged'}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {inputValue !== undefined && (
          <div>
            <div className="text-xs font-mono text-text-dim uppercase tracking-[1px]">Input</div>
            <div className="text-sm font-mono text-text-bright tabular-nums">
              {formatNumber(inputValue, 4)}
            </div>
          </div>
        )}
        {outputValue !== undefined && (
          <div>
            <div className="text-xs font-mono text-text-dim uppercase tracking-[1px]">Output</div>
            <div className="text-sm font-mono text-text-bright tabular-nums">
              {formatNumber(outputValue, 4)}
            </div>
          </div>
        )}
        {iterations !== undefined && (
          <div>
            <div className="text-xs font-mono text-text-dim uppercase tracking-[1px]">Iterations</div>
            <div className="text-sm font-mono text-text-bright tabular-nums">
              {iterations}
            </div>
          </div>
        )}
      </div>
      {message && (
        <div className="text-xs font-mono text-text-muted">{message}</div>
      )}
    </div>
  );
}

interface GoalSeekResultProps {
  state: ToolCallState;
}

/**
 * Phase 4 Session 3 — `goal_seek` iteration ticker.
 * Reads `goal_seek_started` payload (target/input/output/max_iterations) +
 * latest `iteration_completed` event for the search trajectory.
 */
function renderInProgress(state: Extract<ToolCallState, { phase: 'executing' }>) {
  const startedEvent = state.progress.find((p) => p.phase === 'goal_seek_started');
  const iterationEvents = state.progress.filter((p) => p.phase === 'iteration_completed');
  const startedPayload = startedEvent?.payload as
    | { target?: number; output_name?: string; input_name?: string; max_iterations?: number }
    | undefined;
  const target = startedPayload?.target;
  const inputName = startedPayload?.input_name ?? '…';
  const outputName = startedPayload?.output_name ?? '…';
  const maxIter = startedPayload?.max_iterations ?? 50;
  const lastIter = iterationEvents[iterationEvents.length - 1]?.payload as
    | { iteration?: number; current_value?: number; current_output?: number; distance_from_target?: number }
    | undefined;

  const titleSuffix =
    typeof target === 'number'
      ? `: ${outputName} → ${formatNumber(target, 4)}`
      : '';

  return (
    <PhaseShell variant="in-progress" title={`Goal Seek${titleSuffix}`}>
      <div
        className="text-xs font-mono space-y-1"
        data-testid="goal-seek-progress"
      >
        {!startedPayload && (
          <div className="text-text-muted">Initializing search…</div>
        )}
        {startedPayload && (
          <div className="text-text-muted">
            Searching {inputName} for {outputName}
            {typeof target === 'number' ? ` = ${formatNumber(target, 4)}` : ''}
          </div>
        )}
        {lastIter && (
          <div
            className="text-text"
            data-testid="goal-seek-latest-iteration"
            data-iteration={lastIter.iteration ?? 0}
          >
            Iter {lastIter.iteration} / {maxIter} —{' '}
            <span className="text-text-bright tabular-nums">{inputName}</span> ={' '}
            <span className="text-text-bright tabular-nums">
              {formatNumber(lastIter.current_value ?? 0, 4)}
            </span>
            , {outputName} ={' '}
            <span className="text-text-bright tabular-nums">
              {formatNumber(lastIter.current_output ?? 0, 4)}
            </span>
            , distance{' '}
            <span className="text-warn tabular-nums">
              {formatNumber(lastIter.distance_from_target ?? 0, 6)}
            </span>
          </div>
        )}
      </div>
    </PhaseShell>
  );
}

/**
 * Phase-aware GoalSeekResult. Per directive §7.4:
 * - skeleton: stub convergence chart axes; iteration count placeholder.
 * - in-progress: iteration ticker driven by Phase 4 Session 3 progress events.
 * - complete: existing render.
 * - aborted: skeleton with "ABORTED" badge.
 */
export function GoalSeekResult({ state }: GoalSeekResultProps) {
  if (state.phase === 'complete' && isGoalSeekResult(state.result)) {
    return renderComplete(state.result);
  }
  if (state.phase === 'complete') {
    return (
      <div className="text-xs font-mono text-text-muted">
        <pre className="whitespace-pre-wrap">{JSON.stringify(state.result, null, 2)}</pre>
      </div>
    );
  }

  if (state.phase === 'executing') {
    return renderInProgress(state);
  }

  const variant = state.phase === 'aborted' ? 'aborted' : 'skeleton';

  return (
    <PhaseShell variant={variant} title="Goal Seek">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-xs font-mono text-text-dim uppercase tracking-[1px]">Input</div>
          <Skeleton className="h-4 w-12 mt-1" />
        </div>
        <div>
          <div className="text-xs font-mono text-text-dim uppercase tracking-[1px]">Output</div>
          <Skeleton className="h-4 w-12 mt-1" />
        </div>
        <div>
          <div className="text-xs font-mono text-text-dim uppercase tracking-[1px]">Iterations</div>
          <Skeleton className="h-4 w-8 mt-1" />
        </div>
      </div>
    </PhaseShell>
  );
}
